// @ts-nocheck
/**
 * Agent Lead Assignment Detail — redesigned to obd3-gui pattern
 * (ScrollPage + PageHeader + summary Card + shadcn table + submission Dialog).
 * Read-only. The table pages from the server; CSV export fetches every lead
 * once on click and unions submission_data keys across them.
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { ArrowLeft, CheckCheck, Download, Eye, Loader2, Pencil, Trash2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog';
import { useModuleAcl } from '@/hooks/useModuleAcl';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScrollPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import Loader from '@/components/partials/components/Loader';
import { getApiCall, postApiCall } from '@/service/httpCall';
import {
  API_AGENT_LEADS_GET_ONE,
  API_AGENT_LEADS_ITEMS,
  API_AGENT_LEADS_ITEMS_DELETE,
  API_AGENT_LEADS_MARK_COMPLETE,
} from '@/service/url';
import { LeadsPager, usePagedLeads } from './usePagedLeads';
import { EditLeadTitleDialog, transferMovedLabel } from './leadAssignmentUi';
import { canMutateAgentLeads } from '@/utilities/agentLeadsQuota';
import { getUser } from '@/utilities/setting';
import { MODULE_ACL, ROUTES, USER_ROLES } from '@/utilities/constant';
import { exportToCSV } from '@/utilities/helpers';

const STATUS_BADGE: Record<string, [string, string]> = {
  completed: ['Completed', 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400'],
  pending: ['Pending', 'bg-amber-500/15 text-amber-700 dark:text-amber-400'],
  deleted: ['Deleted', 'bg-red-500/15 text-red-700 dark:text-red-400'],
};

const StatusBadge = ({ status }: any) => {
  const [label, tone] = STATUS_BADGE[status] || STATUS_BADGE.pending;
  return (
    <span className={`inline-flex rounded-md px-2 py-0.5 text-xs font-medium ${tone}`}>{label}</span>
  );
};

const AgentLeadAssignmentDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any>(null);
  const [submissionRow, setSubmissionRow] = useState<any>(null);
  const [search, setSearch] = useState('');
  const [exporting, setExporting] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const canMutateLeads = canMutateAgentLeads(getUser()?.role);
  const leadsAcl = useModuleAcl(MODULE_ACL.AGENT_LEADS);
  // Per-lead delete: super admin, or call centre admin holding Agent Leads "delete".
  const canDeleteItems =
    canMutateLeads && (getUser()?.role === USER_ROLES.SUPERADMIN || leadsAcl.canDelete);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [deleteIds, setDeleteIds] = useState<number[] | null>(null);
  const [deletingItems, setDeletingItems] = useState(false);
  const [confirmingComplete, setConfirmingComplete] = useState(false);
  const [completing, setCompleting] = useState(false);
  const paged = usePagedLeads(API_AGENT_LEADS_ITEMS.replace(':id', id), { search }, 50);

  const loadSummary = (initial = false) => {
    if (initial) setLoading(true);
    return getApiCall(`${API_AGENT_LEADS_GET_ONE.replace(':id', id)}?items=0`)
      .then((resp: any) => setData(resp))
      .catch((err: any) => toast.error(friendlyApiError(err) || 'Unable to load assignment'))
      .finally(() => initial && setLoading(false));
  };

  useEffect(() => {
    loadSummary(true);
  }, [id]);

  useEffect(() => setSelectedIds([]), [paged.rows]);

  if (loading) return <Loader />;
  if (!data) return null;

  const items = paged.rows;
  const selectableIds = canDeleteItems
    ? items.filter((it: any) => !it.is_deleted).map((it: any) => Number(it.id))
    : [];
  const allSelected = selectableIds.length > 0 && selectableIds.every((x) => selectedIds.includes(x));
  // A lead list always keeps at least one number (backend enforces it too).
  const remainingLeads = Number(data.total_leads) || 0;
  const keepOneHint = 'A lead list must keep at least one number. Delete the whole lead list instead.';
  const toggleSelected = (itemId: number) =>
    setSelectedIds((prev) =>
      prev.includes(itemId) ? prev.filter((x) => x !== itemId) : [...prev, itemId],
    );

  const confirmDeleteItems = () => {
    if (!deleteIds?.length) return;
    setDeletingItems(true);
    postApiCall(API_AGENT_LEADS_ITEMS_DELETE.replace(':id', id), { item_ids: deleteIds })
      .then(() => {
        toast.success(deleteIds.length === 1 ? 'Lead deleted' : `${deleteIds.length.toLocaleString('en-IN')} leads deleted`);
        setDeleteIds(null);
        setSelectedIds([]);
        paged.reload();
        loadSummary();
      })
      .catch((err: any) => toast.error(friendlyApiError(err) || 'Unable to delete leads'))
      .finally(() => setDeletingItems(false));
  };

  const confirmComplete = () => {
    setCompleting(true);
    postApiCall(API_AGENT_LEADS_MARK_COMPLETE.replace(':id', id), {})
      .then(() => {
        toast.success('Assignment marked as completed');
        setConfirmingComplete(false);
        paged.reload();
        loadSummary();
      })
      .catch((err: any) => toast.error(friendlyApiError(err) || 'Unable to mark as completed'))
      .finally(() => setCompleting(false));
  };

  const formatSubmissionValue = (v: any) => {
    if (Array.isArray(v)) return v.length ? v.join(', ') : '';
    if (typeof v === 'boolean') return v ? 'Yes' : 'No';
    if (v && typeof v === 'object') return JSON.stringify(v);
    return v === null || v === undefined ? '' : String(v);
  };

  const currentTargetLabel = () =>
    data.assignment_type === 'call_group'
      ? `Call Group: ${data.call_group?.group_name || data.call_group_id || '—'}`
      : Array.isArray(data.agents) && data.agents.length
        ? data.agents
            .map((a: any) => a.agent_name || a.agent_number || `#${a.id}`)
            .join(', ')
        : '—';

  const transferredFromLabel = () => {
    const from = data.transferred_from;
    if (!from) return '';
    const parts: any[] = [];
    const groupIds = Array.isArray(from.call_group_ids) ? from.call_group_ids : [];
    groupIds.forEach((id: any) => parts.push(`Call Group #${id}`));
    const fromAgents = Array.isArray(data.transferred_from_agents)
      ? data.transferred_from_agents
      : [];
    if (fromAgents.length) {
      fromAgents.forEach((a: any) =>
        parts.push(a.agent_name || a.agent_number || `#${a.id}`),
      );
    } else {
      (Array.isArray(from.agent_ids) ? from.agent_ids : []).forEach((id: any) =>
        parts.push(`#${id}`),
      );
    }
    return parts.join(', ');
  };

  const handleExport = () => {
    setExporting(true);
    getApiCall(API_AGENT_LEADS_GET_ONE.replace(':id', id))
      .then((full: any) => exportAll(Array.isArray(full?.items) ? full.items : []))
      .catch((err: any) => toast.error(friendlyApiError(err) || 'Unable to export leads'))
      .finally(() => setExporting(false));
  };

  const exportAll = (items: any[]) => {
    if (!items.length) return toast.info('No leads to export');

    // Union of submission-data keys across all leads → one column each.
    const submissionKeys: string[] = [];
    items.forEach((it: any) => {
      if (it.submission_data && typeof it.submission_data === 'object') {
        Object.keys(it.submission_data).forEach((k) => {
          if (!submissionKeys.includes(k)) submissionKeys.push(k);
        });
      }
    });

    const rowNo = new Map(items.map((r, i) => [r, i + 1]));
    const transferredFrom = transferredFromLabel();
    const transferredTo = data.transferred_at ? currentTargetLabel() : '';
    const transferredBy = data.transferred_by_user?.username || '';
    const transferredAt = data.transferred_at
      ? new Date(data.transferred_at).toLocaleString()
      : '';

    const columns = [
      { header: '#', accessor: (r: any) => rowNo.get(r) },
      { header: 'Assignment Title', accessor: () => data.title || '-' },
      { header: 'Contact Group', accessor: () => data.contact_group?.group_name || '-' },
      {
        header: 'Disposition Form',
        accessor: () => data.disposition_form?.name || '-',
      },
      { header: 'Assigned By', accessor: () => data.assigned_by_user?.username || '-' },
      { header: 'Contact Name', accessor: (r: any) => r.contact_name || '-' },
      { header: 'Number', accessor: (r: any) => r.contact_number || '-' },
      { header: 'Status', accessor: (r: any) => r.status || '-' },
      {
        header: 'Completed By',
        accessor: (r: any) => r.completed_by_agent?.agent_name || '-',
      },
      {
        header: 'Completed At',
        accessor: (r: any) =>
          r.completed_at ? new Date(r.completed_at).toLocaleString() : '-',
      },
      { header: 'Transferred From', accessor: () => transferredFrom || '-' },
      { header: 'Transferred To', accessor: () => transferredTo || '-' },
      { header: 'Transferred Leads', accessor: () => transferMovedLabel(data.transferred_from) || '-' },
      { header: 'Transferred By', accessor: () => transferredBy || '-' },
      { header: 'Transferred At', accessor: () => transferredAt || '-' },
      ...submissionKeys.map((k) => ({
        header: k,
        accessor: (r: any) =>
          r.submission_data && r.submission_data[k] !== undefined
            ? formatSubmissionValue(r.submission_data[k])
            : '',
      })),
    ];

    exportToCSV({
      fileName: `agent-lead-${data.id || id}-details`,
      data: items,
      columns,
    });
  };

  const target =
    data.assignment_type === 'call_group'
      ? `Call Group: ${data.call_group?.group_name || data.call_group_id}`
      : Array.isArray(data.agents) && data.agents.length
        ? data.agents
            .map((a: any) => a.agent_name || a.agent_number || `#${a.id}`)
            .join(', ')
        : `${Array.isArray(data.agent_ids) ? data.agent_ids.length : 0} agent(s)`;

  return (
    <ScrollPage className="max-w-none">
      <PageHeader
        title={data.title || 'Lead Assignment'}
        description="Detail of a single lead assignment."
        actions={
          <>
            {canMutateLeads && data.status !== 'completed' ? (
              <Button variant="outline" onClick={() => setConfirmingComplete(true)}>
                <CheckCheck className="h-4 w-4" />
                Mark as completed
              </Button>
            ) : null}
            {canMutateLeads ? (
              <Button variant="outline" onClick={() => setEditingTitle(true)}>
                <Pencil className="h-4 w-4" />
                Edit title
              </Button>
            ) : null}
            <Button
              variant="outline"
              onClick={handleExport}
              disabled={exporting || (!paged.loading && paged.total === 0 && !search)}
            >
              {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              Export
            </Button>
            <Button asChild variant="outline">
              <Link to={`/${ROUTES.AGENT_LEADS.ADMIN_LIST}`}>
                <ArrowLeft className="h-4 w-4" />
                Back
              </Link>
            </Button>
          </>
        }
      />

      <Card className="mb-4">
        <CardHeader>
          <CardTitle className="text-base">Summary</CardTitle>
          <CardDescription>
            Progress: {data.completed_leads || 0}/{data.total_leads || 0}{' '}
            <StatusBadge status={data.status} />
          </CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-3 text-sm md:grid-cols-2">
          <div>
            <span className="text-muted-foreground">Contact Group:</span>{' '}
            <span className="font-medium text-foreground">
              {data.contact_group?.group_name || '—'}
            </span>
          </div>
          {data.campaign && (
            <div>
              <span className="text-muted-foreground">Campaign:</span>{' '}
              <span className="font-medium text-foreground">
                {data.campaign.campaign_name} (#{data.campaign.campaign_id})
              </span>
            </div>
          )}
          <div>
            <span className="text-muted-foreground">Disposition Form:</span>{' '}
            <span className="font-medium text-foreground">
              {data.disposition_form?.name || '—'}
            </span>
          </div>
          <div>
            <span className="text-muted-foreground">Assigned By:</span>{' '}
            <span className="font-medium text-foreground">
              {data.assigned_by_user?.username || '—'}
            </span>
          </div>
          {data.completed_at && (
            <>
              <div>
                <span className="text-muted-foreground">Completed By:</span>{' '}
                <span className="font-medium text-foreground">
                  {data.completed_by_user?.username || '—'}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">Completed At:</span>{' '}
                <span className="font-medium text-foreground">
                  {new Date(data.completed_at).toLocaleString()}
                </span>
              </div>
            </>
          )}
          <div className="md:col-span-2">
            <span className="text-muted-foreground">Target:</span>{' '}
            <span className="font-medium text-foreground">{target}</span>
          </div>
          {data.transferred_at && (
            <div className="md:col-span-2">
              <span className="text-muted-foreground">Transferred:</span>{' '}
              <span className="font-medium text-foreground">
                {new Date(data.transferred_at).toLocaleString()}
                {data.transferred_by_user?.username ? ` by ${data.transferred_by_user.username}` : ''}
                {transferredFromLabel() ? ` · from ${transferredFromLabel()}` : ''}
                {transferMovedLabel(data.transferred_from)
                  ? ` · ${transferMovedLabel(data.transferred_from)} moved`
                  : ''}
              </span>
              {data.transferred_from?.assignment_id ? (
                <>
                  {' · split from '}
                  <Link
                    to={`/${ROUTES.AGENT_LEADS.ADMIN_LIST}/${data.transferred_from.assignment_id}`}
                    className="font-medium text-foreground underline-offset-2 hover:underline"
                  >
                    assignment #{data.transferred_from.assignment_id}
                  </Link>
                </>
              ) : null}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="mb-2 flex items-center justify-end gap-2">
        {canDeleteItems && selectedIds.length > 0 ? (
          <span title={selectedIds.length >= remainingLeads ? keepOneHint : undefined}>
            <Button
              variant="destructive"
              size="sm"
              disabled={selectedIds.length >= remainingLeads}
              onClick={() => setDeleteIds(selectedIds)}
            >
              <Trash2 className="h-4 w-4" />
              Delete selected ({selectedIds.length.toLocaleString('en-IN')})
            </Button>
          </span>
        ) : null}
        <Input
          className="max-w-xs"
          placeholder="Search name or number"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      <div className="overflow-hidden rounded-md border border-border">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40">
              <tr>
                {canDeleteItems ? (
                  <th className="w-10 px-3 py-2 text-left">
                    <Checkbox
                      aria-label="Select all leads on this page"
                      disabled={selectableIds.length === 0}
                      checked={allSelected ? true : selectedIds.length > 0 ? 'indeterminate' : false}
                      onCheckedChange={() => setSelectedIds(allSelected ? [] : selectableIds)}
                    />
                  </th>
                ) : null}
                <th className="w-10 px-3 py-2 text-left font-medium">#</th>
                <th className="px-3 py-2 text-left font-medium">Contact Name</th>
                <th className="px-3 py-2 text-left font-medium">Number</th>
                <th className="px-3 py-2 text-left font-medium">Status</th>
                <th className="px-3 py-2 text-left font-medium">Completed By</th>
                <th className="px-3 py-2 text-left font-medium">Completed At</th>
                <th className="px-3 py-2 text-right font-medium">Submission</th>
                {canDeleteItems ? <th className="w-12 px-3 py-2" /> : null}
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan={canDeleteItems ? 9 : 7} className="px-3 py-8 text-center text-muted-foreground">
                    {paged.loading ? 'Loading…' : search ? 'No leads match your search.' : 'No leads in this assignment.'}
                  </td>
                </tr>
              ) : (
                items.map((it: any, idx: number) => (
                  <tr key={it.id} className={`border-t border-border${it.is_deleted ? ' bg-red-500/5 text-muted-foreground' : ''}`}>
                    {canDeleteItems ? (
                      <td className="px-3 py-2">
                        {it.is_deleted ? null : (
                          <Checkbox
                            aria-label={`Select lead ${it.contact_number}`}
                            checked={selectedIds.includes(Number(it.id))}
                            onCheckedChange={() => toggleSelected(Number(it.id))}
                          />
                        )}
                      </td>
                    ) : null}
                    <td className="px-3 py-2 text-xs text-muted-foreground">{(paged.page - 1) * paged.limit + idx + 1}</td>
                    <td className="px-3 py-2 font-medium">{it.contact_name || '—'}</td>
                    <td className="px-3 py-2 font-mono text-xs">
                      {it.contact_number}
                    </td>
                    <td className="px-3 py-2">
                      <StatusBadge status={it.is_deleted ? 'deleted' : it.status} />
                      {it.is_deleted && it.deleted_at ? (
                        <div className="mt-1 text-xs text-muted-foreground">
                          {new Date(it.deleted_at).toLocaleString()}
                          {it.deleted_by_user?.username ? ` by ${it.deleted_by_user.username}` : ''}
                        </div>
                      ) : null}
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">
                      {it.completed_by_agent?.agent_name || '—'}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">
                      {it.completed_at
                        ? new Date(it.completed_at).toLocaleString()
                        : '—'}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {it.status === 'completed' &&
                      it.submission_data &&
                      Object.keys(it.submission_data).length > 0 ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setSubmissionRow(it)}
                        >
                          <Eye className="h-4 w-4" />
                          View
                        </Button>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </td>
                    {canDeleteItems ? (
                      <td className="px-3 py-2 text-right">
                        {it.is_deleted ? null : (
                          <span title={remainingLeads <= 1 ? keepOneHint : undefined}>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8 text-destructive hover:text-destructive"
                            aria-label={`Delete lead ${it.contact_number}`}
                            disabled={remainingLeads <= 1}
                            onClick={() => setDeleteIds([Number(it.id)])}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                          </span>
                        )}
                      </td>
                    ) : null}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <LeadsPager page={paged.page} total={paged.total} limit={paged.limit} onPage={paged.setPage} />
      </div>

      <Dialog
        open={!!submissionRow}
        onOpenChange={(o) => !o && setSubmissionRow(null)}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              Submission — {submissionRow?.contact_name || submissionRow?.contact_number}
            </DialogTitle>
          </DialogHeader>
          {submissionRow?.submission_data &&
          Object.keys(submissionRow.submission_data).length > 0 ? (
            <div className="overflow-hidden rounded-md border border-border">
              <table className="w-full text-sm">
                <tbody>
                  {Object.entries(submissionRow.submission_data).map(([k, v]: any) => {
                    let display: any;
                    if (Array.isArray(v)) display = v.length ? v.join(', ') : '—';
                    else if (typeof v === 'boolean') display = v ? 'Yes' : 'No';
                    else if (v && typeof v === 'object') display = JSON.stringify(v);
                    else
                      display =
                        v === null || v === undefined || v === '' ? '—' : String(v);
                    return (
                      <tr key={k} className="border-t border-border first:border-t-0">
                        <th className="w-2/5 bg-muted/40 px-3 py-2 text-left font-medium">
                          {k}
                        </th>
                        <td className="px-3 py-2 break-all">{display}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-sm text-muted-foreground">No submission data.</div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSubmissionRow(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <EditLeadTitleDialog
        assignment={editingTitle ? data : null}
        onClose={() => setEditingTitle(false)}
        onSaved={(title) => setData((prev: any) => ({ ...prev, title }))}
      />
      <ConfirmDialog
        open={deleteIds !== null}
        onOpenChange={(o) => !o && !deletingItems && setDeleteIds(null)}
        title={deleteIds?.length === 1 ? 'Delete Lead' : 'Delete Leads'}
        description={
          deleteIds?.length === 1
            ? 'Delete this lead? Agents will no longer see it. This cannot be undone.'
            : `Delete ${(deleteIds?.length || 0).toLocaleString('en-IN')} selected leads? Agents will no longer see them. This cannot be undone.`
        }
        confirmLabel="Delete"
        tone="destructive"
        loading={deletingItems}
        onConfirm={confirmDeleteItems}
      />
      <ConfirmDialog
        open={confirmingComplete}
        onOpenChange={(o) => !o && !completing && setConfirmingComplete(false)}
        title="Mark Assignment as Completed"
        description={`Mark this assignment as completed? Its ${Math.max(0, (data.total_leads || 0) - (data.completed_leads || 0)).toLocaleString('en-IN')} pending lead(s) will be closed and agents will no longer see them as pending.`}
        confirmLabel="Mark as completed"
        loading={completing}
        onConfirm={confirmComplete}
      />
    </ScrollPage>
  );
};

export default AgentLeadAssignmentDetail;
